import { Clear, MoreVert, SaveAlt } from '@mui/icons-material';
import { Box, Button, IconButton, Menu, styled, TextField } from '@mui/material';
import {
  DataGridPro,
  GridCsvExportOptions,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { FC, useState } from 'react';

export interface DataGridToolbarProps {
  csvFileName?: string;
  onClearFilters?: () => void;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  searchPlaceholder?: string;
}

export interface DataGridEmptyProps {
  emptyRowsMessage: string;
}

const DataGridStriped = styled(DataGridPro)(() => ({
  border: 'none',
  '.MuiDataGrid-columnHeaders': {
    borderBottom: 'none',
    backgroundColor: '#FAFAFA',
  },
  '.MuiDataGrid-row': {
    ':hover': {
      cursor: 'pointer',
    },
  },
  '.MuiDataGrid-row:nth-of-type(2n)': {
    backgroundColor: '#FAFAFA',
  },
  '.MuiDataGrid-columnHeader': {
    backgroundColor: '#FAFAFA',
  },
  '.MuiDataGrid-columnHeaderTitle': {
    color: '#222222',
    fontWeight: 600,
    fontSize: '16px',
  },
}));

export const DataGridToolbar: FC<DataGridToolbarProps> = (props: DataGridToolbarProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const apiRef = useGridApiContext();

  const handleExport = (options?: GridCsvExportOptions) => apiRef.current.exportDataAsCsv(options);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    event.preventDefault();
    props.setSearchValue(event.currentTarget.value);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box display="flex" gap="16px" mb={2} alignItems="center">
      <TextField
        sx={{
          maxWidth: 300,
          '& .MuiOutlinedInput-root': {
            height: 40,
            fontSize: '16px',
            color: '#222222',
          },
          '& .MuiOutlinedInput-input': {
            padding: '0 12px',
            height: '40px',
            boxSizing: 'border-box',
          },
        }}
        value={props.searchValue}
        onChange={handleChange}
        variant="outlined"
        fullWidth
        placeholder={props.searchPlaceholder || 'Search'}
      />
      <Box sx={{ gap: '16px', display: { xs: 'none', sm: 'flex' } }} flex={1}>
        <GridToolbarFilterButton sx={{ backgroundColor: '#F2FAF8', padding: '8px', textWrap: 'nowrap' }} />
        <GridToolbarColumnsButton sx={{ backgroundColor: '#F2FAF8', padding: '8px', textWrap: 'nowrap' }} />
        {props.onClearFilters && (
          <Button
            sx={{ backgroundColor: '#F2FAF8', padding: '8px', textWrap: 'nowrap' }}
            startIcon={<Clear />}
            onClick={props.onClearFilters}
            size="small"
          >
            Clear Filters
          </Button>
        )}
      </Box>
      <Box flex={1} />
      <Box sx={{ gap: '16px', display: { xs: 'none', sm: 'flex' } }}>
        {props.csvFileName && (
          <Button
            onClick={() => handleExport({ fileName: props.csvFileName })}
            startIcon={<SaveAlt />}
            sx={{ backgroundColor: '#F2FAF8', padding: '8px' }}
          >
            Export
          </Button>
        )}
      </Box>
      <IconButton onClick={handleClick} sx={{ display: { xs: 'flex', sm: 'none' } }} size="large">
        <MoreVert />
      </IconButton>
      <Menu anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} anchorEl={anchorEl} open={open} onClose={handleClose}>
        <Box display="flex" flexDirection="column">
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          {props.csvFileName && <GridToolbarExport csvOptions={{ fileName: props.csvFileName }} />}
          {props.onClearFilters && (
            <Button startIcon={<Clear />} onClick={props.onClearFilters} size="small">
              Clear Filters
            </Button>
          )}
        </Box>
      </Menu>
    </Box>
  );
};

export default DataGridStriped;
